// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { styledSlider } from "@bg/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const ProductCarouselComponent = React.lazy(() => import(`@${BLUEPRINT_NAME}/App/Components/ProductCarouselComponent`));
const WidgetTitle = React.lazy(() => import(`@${BLUEPRINT_NAME}/App/Components/WidgetTitle`));

const enum fields {
    title = "title",
    showImage = "showImage",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImage]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        isAuthenticated: state.context.session.isAuthenticated,
        isLoading: state.xFeaturedProducts.isLoading,
        featuredProducts: getProductsForProductInfoList(state, props.id),
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface FeaturedProductsStyles {}

export const styles: FeaturedProductsStyles = {};

export const featuredProductsContainer = css`
    ${styledSlider()}
    .swiper-container {
        width: 100% !important;
        padding: 15px;
    }

    .swiper-slide {
        display: flex;
        justify-content: center;
        @media screen and (max-width: 1329px) {
            min-width: 0;
        }
    }
`;

const PAGE_SIZE = 16;

const FeaturedProducts = ({
    id,
    fields,
    isAuthenticated,
    isLoading,
    featuredProducts,
    loadCarouselProducts,
    customSettings,
}: Props) => {
    useEffect(() => {
        if (
            (typeof window === "undefined" || !window.localStorage.getItem("postalCodeID")?.length) &&
            !isAuthenticated &&
            customSettings &&
            customSettings.postalCodeLocationFinder.toLowerCase() === "true"
        ) {
            return;
        }
        loadCarouselProducts({
            carouselId: id,
            carouselType: "featuredProducts",
            displayProductsFrom: "",
            relatedProductType: "",
            seedWithManuallyAssigned: "",
            selectedCategoryIds: [],
            numberOfProductsToDisplay: PAGE_SIZE,
            isProductDetailsPage: false,
            isProductListPage: false,
            category: undefined,
            isBrandDetailsPage: false,
            brand: undefined,
        });
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper
                css={css`
                    height: 100px;
                    width: 100%;
                    padding: 10px 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!featuredProducts || !featuredProducts.length) {
        return null;
    }

    const { title } = fields;
    const isBg = BLUEPRINT_NAME === "bg";
    return (
        <React.Suspense fallback={<LoadingOverlay loading={true} />}>
            {title && <WidgetTitle title={title} icon={isBg ? "FeaturedProducts" : undefined} />}
            <ProductCarouselComponent id={id} products={featuredProducts} fields={fields} />
        </React.Suspense>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    icon: "Carousel",
    displayName: "Featured Products",
    fieldDefinitions: [
        {
            displayName: "Section Title",
            name: fields.title,
            editorTemplate: "TextField",
            defaultValue: "Featured Products",
            fieldType: "Translatable",
        },
        {
            name: fields.showImage,
            displayName: "Image",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "General",
            sortOrder: 8,
        },
        {
            name: fields.showPartNumbers,
            displayName: "Part Numbers",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 11,
            isVisible: () => BLUEPRINT_NAME !== "bgs",
        },
        {
            name: fields.showPrice,
            displayName: "Price",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 12,
        },
        {
            name: fields.showAddToCart,
            displayName: "Add to Cart",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 13,
        },
        {
            name: fields.showAddToList,
            displayName: "Add to List",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 14,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedProducts),
    definition,
};

export default widgetModule;
